import {
  Component,
  Suspense,
  forwardRef,
  useCallback,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/utils/logger";
import { PdfPagesRendererLazy } from "./PdfPagesRenderer.lazy";
import type { PdfPagesRendererProps } from "./PdfPagesRenderer";
import type { PdfPagesRendererHandle } from "./types";

export interface PdfRendererBoundaryProps extends Omit<PdfPagesRendererProps, "onLoadError"> {
  /** What to show instead of pdf.js — the native-viewer `PDFRenderer`. */
  fallback: ReactNode;
  /** Called once when the boundary gives up on pdf.js and shows `fallback`. */
  onFallback?: (error: Error) => void;
}

interface CatcherProps {
  children: ReactNode;
  fallback: ReactNode;
  onError: (error: Error) => void;
}

interface CatcherState {
  failed: boolean;
}

/**
 * Catches a render-time failure of the lazy chunk (a stale hash the reload
 * guard in `lazyWithReload` could not recover, an unsupported browser, a
 * pdf.js exception during render) and hands over to the fallback.
 */
class PdfChunkCatcher extends Component<CatcherProps, CatcherState> {
  state: CatcherState = { failed: false };

  static getDerivedStateFromError(): CatcherState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void logger.warn("ui", "pdf-renderer", "pdf.js renderer failed — using the native viewer", {
      error: error.message,
      componentStack: info.componentStack,
    });
    this.props.onError(error);
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function PdfLoadingSkeleton() {
  return (
    <div className="flex h-full min-h-0 w-full flex-col" data-testid="pdf-renderer-skeleton">
      <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-14" />
        <Skeleton className="h-8 w-8" />
        <Skeleton className="ml-auto h-8 w-24" />
      </div>
      <div className="flex min-h-0 flex-1 items-start justify-center overflow-hidden bg-gray-100 p-4">
        <Skeleton className="aspect-[595/842] h-full max-h-full w-full max-w-3xl rounded-none bg-white" />
      </div>
    </div>
  );
}

/**
 * Mounts the lazy pdf.js renderer and degrades to `fallback` when either the
 * chunk cannot be loaded or pdf.js reports that it cannot load / parse the
 * document. Mount with `key={docId}` so a new document gets a fresh attempt.
 */
export const PdfRendererBoundary = forwardRef<PdfPagesRendererHandle, PdfRendererBoundaryProps>(
  function PdfRendererBoundary({ fallback, onFallback, ...rendererProps }, ref) {
    const [failed, setFailed] = useState(false);
    // The catcher and the load-error path can both report; tell the parent once.
    const reportedRef = useRef(false);

    const handleFailure = useCallback(
      (error: Error) => {
        setFailed(true);
        if (reportedRef.current) return;
        reportedRef.current = true;
        onFallback?.(error);
      },
      [onFallback]
    );

    const handleLoadError = useCallback(
      (error: Error) => {
        void logger.warn(
          "ui",
          "pdf-renderer",
          "pdf.js could not load the document — using the native viewer",
          {
            error: error.message,
          }
        );
        handleFailure(error);
      },
      [handleFailure]
    );

    if (failed) return <>{fallback}</>;

    return (
      <PdfChunkCatcher fallback={fallback} onError={handleFailure}>
        <Suspense fallback={<PdfLoadingSkeleton />}>
          <PdfPagesRendererLazy ref={ref} {...rendererProps} onLoadError={handleLoadError} />
        </Suspense>
      </PdfChunkCatcher>
    );
  }
);
